import { JsonFetch } from "@roc-digital/mxm-base/logic";
import type { FeFile, FeFileFilters } from "@roc-digital/types/files";
import React from "react";
import { uploadImageToBucket } from "./upload";
import { PaginatedResponse } from "@roc-digital/types";
export type { FeFile, FeFileFilters } from "@roc-digital/types/files";
import ExifReader from 'exifreader';

export async function listFiles(filters?: FeFileFilters) {
  const params = toQueryParams(filters || {});
  return await JsonFetch<PaginatedResponse<FeFile>>({
    method: 'GET',
    url: `${process.env.REACT_APP_AUX_API_URL}/admin/files?${params}`,
  });
}

export async function readFile(id: string) {
  return await JsonFetch<FeFile>({
    method: 'GET',
    url: `${process.env.REACT_APP_AUX_API_URL}/admin/files/${id}`,
  });
}

export async function createFile(file: FeFile): Promise<FeFile> {
  return await JsonFetch<FeFile>({
    method: 'POST',
    url: `${process.env.REACT_APP_AUX_API_URL}/admin/files`,
    data: file
  });
}

export async function updateFile(file: FeFile): Promise<FeFile> {
  return await JsonFetch<FeFile>({
    method: 'PATCH',
    url: `${process.env.REACT_APP_AUX_API_URL}/admin/files/${file.id}`,
    data: file
  });
}

export async function deleteFile(id: string) {
  return await JsonFetch<void>({
    method: 'DELETE',
    url: `${process.env.REACT_APP_AUX_API_URL}/admin/files/${id}`,
  });
}

export function toQueryParams(params: any) {
  const query = Object.keys(params).filter(key => {
    return params[key] !== 'undefined' && params[key] !== 'null' && params[key] !== null && params[key] !== undefined && params[key] !== '';
  })
  .map(key => `${key}=${params[key]}`)
  .join('&');
  return query;
}
/////

export async function renameFile(id: string, name: string) {
  const file = await readFile(id);
  file.name = name;
  await updateFile(file);
}

export async function archiveFile(id: string, archive: boolean) {
  const file = await readFile(id);
  file.archived = archive ? Date.now() : null as any;
  await updateFile(file);
}

/////

export async function uploadFiles(files: File[]) {
  uploadState.showDrawer = true;
  const current = new Set(uploadState.uploads.map(u => u.file.webkitRelativePath || u.file.name));
  files = files.filter(f => current.has(f.webkitRelativePath || f.name) === false);

  const uploads = files.map(file => {
    const upload: FileUploadStatus = {
      id: Math.random().toString(),
      file,
      status: 'queued'
    };
    return upload
  });
  uploadState.uploads.push(...uploads);
  processUploadQueue();
}

let isRunning = false;
async function processUploadQueue() {
  if(isRunning) return;

  isRunning = true;
  for(let i = 0; i < uploadState.uploads.length; i++) {
    const upload = uploadState.uploads[i];
    if(upload.status !== 'queued') continue;
    try {
      await uploadFile(upload);
    } catch(err) {
      console.error(err);
      upload.status = 'error';
      updateUploadState();
    }
  }
  isRunning = false;
}

async function uploadFile(upload: FileUploadStatus) {
  upload.status = 'uploading';
  updateUploadState();

  const attr = await loadImageAttributes(upload.file);
  const stdAttributes = findStandardAttributes(attr);
  console.log(stdAttributes);

  if(!upload.imageUrl) {
    const result: {message: string, publicPath: string} = await uploadImageToBucket(upload.file);
    upload.imageUrl = result.publicPath;
  }

  if(!upload.imageUrl) throw new Error('Upload has no url.');

  let feFile: FeFile = {
    id: '',
    mime: upload.file.type,
    name: stdAttributes.title || upload.file.name,
    src: upload.imageUrl,
    meta: {
      size: upload.file.size,
      originalName: upload.file.name,
      originalModification: upload.file.lastModified,
      ...stdAttributes,
      // attributes: attr,
    }
  }

  feFile = await createFile(feFile);

  upload.feFile = feFile;
  upload.status = 'finished';
  updateUploadState();
}

export interface FileUploadStatus {
  id: string;
  file: File;
  imageUrl?: string;
  status: 'queued' | 'uploading' | 'error' | 'finished' | 'canceled';
  feFile?: FeFile,
}

interface UploadState {
  uploads: FileUploadStatus[];
  showDrawer: boolean
}

const uploadEvents = new EventTarget();
let uploadState = {uploads: [], showDrawer: false} as UploadState;

export function hideUploadsDrawer() {
  uploadState.showDrawer = false;
  updateUploadState();
}

export function useUploadState() {
  const [current, setNext] = React.useState(uploadState);
  React.useEffect(() => {
    const cb = () => {
      setNext(uploadState);
    };

    uploadEvents.addEventListener('next', cb);

    return () => {
      uploadEvents.removeEventListener('next', cb);
    }
  });

  return current;
}

export function retryUpload(id: string) {
  const index = uploadState.uploads.findIndex(u => u.id === id);
  if(index === -1) return;

  const upload = uploadState.uploads[index];
  if(upload.status !== 'error') return;
  upload.status = 'queued';
  updateUploadState();
  processUploadQueue();
}

export function canceledUpload(id: string) {
  const index = uploadState.uploads.findIndex(u => u.id === id);
  if(index === -1) return;

  const upload = uploadState.uploads[index];
  if(upload.status !== 'queued') return;
  upload.status = 'canceled';
  updateUploadState();
}

export function updateUploadState() {
  uploadState = {...uploadState};
  uploadEvents.dispatchEvent(new Event('next'));
}


async function loadImageAttributes(file: File | string) {
  const rawTags = await ExifReader.load(file, {async: true});

  const attr: {[key: string]: string} = {};

  Object.keys(rawTags).forEach(key => {
    const tag = rawTags[key];
    const value = (typeof tag.value === 'string' ? tag.value : '') || tag.description;
    attr[key] = value;
  });

  return attr;
}


interface ImageAttributes {
  title: string;
  description: string;
  creator: string;
  credit: string;
  attribution: string;
}

function findStandardAttributes(attrs: {[key: string]: string} ) {
  const std: ImageAttributes = {
    creator: findValuesForKeys(attrs, ['Artist', 'Creator','By-line']),
    credit: findValuesForKeys(attrs, ['Credit']), 
    description: findValuesForKeys(attrs, ['description', 'Caption/Abstract','ImageDescription']),
    title: findValuesForKeys(attrs, ['title', 'Headline','Object Name']),
    attribution: '',
  };

  if(std.creator && std.credit) {
    std.attribution = `(${std.credit} Photo/${std.creator})`;
  } else if(std.credit) {
    std.attribution = `(${std.credit} Photo)`;
  } else {
    std.attribution = std.description;
  }

  return std;
}

function findValuesForKeys(attrs: {[key: string]: string}, keys: string[]): string {
  for(let key of keys) {
    if(attrs[key]) return attrs[key];
    if(attrs[key.toLowerCase()]) return attrs[key.toLowerCase()];
    if(attrs[key.toUpperCase()]) return attrs[key.toUpperCase()];
  }

  return '';
}